// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wiki semantic ui integration
// =============================================================================
// ---Third-party imports
import React, {Component} from 'react'
import {Label, Icon, Grid, Segment} from 'semantic-ui-react'

// ---Framework imports
import log from 'uix/logging'
import {has, get, dict, keys, strip, values, size, filterItems, isNumber} from 'ldash'
import {decodeAttribH5} from 'uix/utils'
import {XMenu} from 'uix/components'
import {RGrid} from 'uix/components/grid'

// ---Local imports
import {
  GenericTagHandler,
  GenericTagHandlerWithAttrFilter,
  SUI_MENU_ITEM_ATTRS,
  SUI_COLORS,
  SUI_ICONS,
  SUI_SIZES,
  bools,
} from './utils'

// ---Globals

// we need to rename the sui Label to XLabel since the gumbo html5 parser will lowercase "label"
const XLABEL_ATTRS = {
  icon: SUI_ICONS,
  corner: ['left', 'right'],
  ribbon: ['true', 'false', 'right'],
  size: SUI_SIZES,
  color: SUI_COLORS,
  detail: ['custom'],
  ...bools(['active', 'basic', 'circular', 'empty', 'floating', 'horizontal', 'tag']),
}
export const xlabel = GenericTagHandler(
  /* we need to rename the sui Label to XLabel since the gumbo html5 parser will lowercase "label"
   */
  'XLabel',
  ({attrib, applyTemplates, text, children}) => {
    let props = decodeAttribH5(attrib)
    let icon = props.icon ? <Icon name={props.icon} /> : null

    props = dict(filterItems(props, name => XLABEL_ATTRS[name]))

    props.color = size(props.color) > 2 ? props.color : undefined
    if (icon || children.length == 0 || !text) {
      return (
        <Label {...props}>
          {icon}
          {text}
          {applyTemplates(children)}
        </Label>
      )
    }
    return <Label {...props} />
  },
  XLABEL_ATTRS,
  `see https://react.semantic-ui.com/elements/label/`
)

const textAlign = ['left', 'center', 'right', 'justified']
const verticalAlign = ['bottom', 'middle', 'top']
const COLS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '16',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'fifteen',
  'sixteen',
]
const DEVICES = ['large screen', 'widescreen', 'computer', 'tablet', 'mobile']

export const grid = {
  match: ({tag}) => tag == 'Grid',
  build: ({attrib, applyTemplates, children}) => {
    let {stackable, ...props} = decodeAttribH5(attrib)
    let stackOn = isNumber(stackable) ? stackable : 800
    return (
      <RGrid stackOn={stackOn} stackable={!!stackable} {...props}>
        {applyTemplates(children)}
      </RGrid>
    )
  },
  completion: ({}) => {
    return {
      '!top': ['Grid'],
      Grid: {
        attrs: {
          divided: ['true', 'false', 'vertically'],
          celled: ['true', 'false', 'internally'],
          padded: ['horizontally', 'vertically'],
          relaxed: ['true', 'false', 'very'],
          stackable: ['true', 'false', '600'],
          textAlign,
          verticalAlign,
          ...bools(['centered', 'container', 'doubling', 'inverted', 'stretched']),
        },
      },
    }
  },
  docs: `see https://react.semantic-ui.com/collections/grid/
  stackable is configurable in pixel/width
  `,
}

export const gridrow = {
  match: ({tag}) => tag == 'Grid.Row',
  build: ({attrib, applyTemplates, children, text}) => {
    let props = decodeAttribH5(attrib)
    return <Grid.Row {...props}>{[text, applyTemplates(children)]}</Grid.Row>
  },
  completion: ({}) => {
    return {
      '!top': ['Grid.Row'],
      'Grid.Row': {
        attrs: {
          divided: ['true', 'false', 'vertically'],
          celled: ['true', 'false', 'internally'],
          padded: ['horizontally', 'vertically'],
          columns: [...COLS, 'equal'],
          color: SUI_COLORS,
          only: DEVICES,
          textAlign,
          verticalAlign,
          ...bools(['centered', 'stretched']),
        },
      },
    }
  },
  docs: `see https://react.semantic-ui.com/collections/grid/`,
}

export const gridcol = {
  match: ({tag}) => tag == 'Grid.Column',
  build: ({attrib, applyTemplates, children, text}) => {
    let props = decodeAttribH5(attrib)
    return <Grid.Column {...props}>{[text, applyTemplates(children)]}</Grid.Column>
  },
  completion: ({}) => {
    return {
      '!top': ['Grid.Column'],
      'Grid.Column': {
        attrs: {
          width: COLS,
          only: DEVICES,
          color: SUI_COLORS,
          textAlign,
          verticalAlign,
          computer: COLS,
          largeScreen: COLS,
          mobile: COLS,
          tablet: COLS,
          widescreen: COLS,
          ...bools(['stretched']),
        },
      },
    }
  },
  docs: `see https://react.semantic-ui.com/collections/grid/`,
}

export const segment = GenericTagHandlerWithAttrFilter(
  'Segment',
  ({attrib, applyTemplates, text, children}) => (
    <Segment {...attrib}>{[text, applyTemplates(children)]}</Segment>
  ),
  {
    attached: ['true', 'false', 'top', 'bottom'],
    floated: ['left', 'right'],
    padded: ['true', 'false', 'very'],
    textAlign: ['left', 'center', 'right'],
    size: SUI_SIZES,
    ...bools([
      'basic',
      'circular',
      'clearing',
      'compact',
      'disabled',
      'inverted',
      'piled',
      'raised',
      'secondary',
    ]),
  },
  `A segment is used to create a grouping of related content.
  see https://react.semantic-ui.com/elements/segment/`
)

export const xmenu = GenericTagHandlerWithAttrFilter(
  'XMenu',
  ({attrib, applyTemplates, children}) => <XMenu {...attrib}>{applyTemplates(children)}</XMenu>,
  {
    attached: ['top', 'bottom'],
    tabular: ['true', 'false', 'right'],
    //~ fixed: ['left', 'right', 'bottom', 'top'],
    color: SUI_COLORS,
    className: [],
    size: SUI_SIZES,
    ...bools([
      'borderless',
      'compact',
      'fluid',
      'inverted',
      'secondary',
      'stackable',
      'vertical',
      'text',
      'padded',
    ]),
  },
  `A menu displays grouped navigation actions.
see https://react.semantic-ui.com/collections/menu/`
)

export const xmenu_item = GenericTagHandlerWithAttrFilter(
  'XMenu.Item',
  ({attrib}) => <XMenu.Item {...attrib}></XMenu.Item>,
  SUI_MENU_ITEM_ATTRS,
  `A menu can contain an item.`
)

export const xmenu_beginright = GenericTagHandlerWithAttrFilter(
  'XMenu.BeginRight',
  ({}) => <XMenu.BeginRight />,
  {},
  `A pseudo menu item that forces all following items to the right.`
)
